import { ReturnTaskReadable, tasksCompleteCreateMutation } from '@crm/api'
import { DynamicFormComponent, TaskFormElement } from '@crm/components'
import { ElementType } from '@crm/types'
import { omitKeys } from '@crm/utils'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Col, Form, Input, Row } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

type Props = {
  task?: ReturnTaskReadable | null
  formName: string
  elements: TaskFormElement[]
  onSuccess: () => void
}

type VisibilityMap = Record<string, boolean>

const isMapEqual = (a: VisibilityMap, b: VisibilityMap) =>
  JSON.stringify(a) === JSON.stringify(b)

export const CompleteTaskForm = ({
  task,
  formName,
  elements,
  onSuccess
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const [visibility, setVisibility] = useState({})

  const queryClient = useQueryClient()

  const normalizedElements = useMemo(
    () => (Array.isArray(elements) ? elements : []),
    [elements]
  )

  const { mutate } = useMutation({
    ...tasksCompleteCreateMutation(),
    onSuccess: () => {
      queryClient.refetchQueries()
      onSuccess()
    },
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const formFields = await form.validateFields()

    const fileElement = elements?.find((i) => i.type === ElementType.FILE)

    if (fileElement) {
      const formFileField =
        formFields[fileElement.name] && formFields[fileElement.name][0]

      if (formFileField) {
        formFields[fileElement.name] = {
          name: formFileField.name,
          file: formFileField.url
        }
      }
    }

    const omit = Object.keys(formFields).filter((key) => !formFields[key])

    mutate({
      path: {
        id: Number(task?.id)
      },
      body: omitKeys(formFields, omit)
    })
  }

  const computeVisibility = useCallback(
    (values: Record<string, unknown>): VisibilityMap =>
      Object.fromEntries(
        normalizedElements.map((el) => {
          const rule = el.props?.rule
          if (!rule || Object.keys(rule).length === 0) return [el.name, true]
          const visible = Object.entries(rule).every(
            ([k, expected]) => values[k] === expected
          )

          return [el.name, visible]
        })
      ),
    [normalizedElements]
  )

  const onValuesChange = useCallback(
    (_changedValues: unknown, allValues: Record<string, unknown>) => {
      const next = computeVisibility(allValues)
      if (!isMapEqual(visibility, next)) setVisibility(next)
    },
    [computeVisibility, visibility]
  )

  useEffect(() => {
    const initial = computeVisibility(form.getFieldsValue())
    if (!isMapEqual(visibility, initial)) setVisibility(initial)
  }, [computeVisibility, normalizedElements])

  return (
    <Form
      name={formName}
      form={form}
      onValuesChange={onValuesChange}
      onFinish={onSubmit}
      layout="vertical"
    >
      <Row>
        <Col span={24}>
          {elements?.length ? (
            <DynamicFormComponent elements={elements} visibility={visibility} />
          ) : (
            <Form.Item name="comment" label={t('contacts.comment')}>
              <Input.TextArea placeholder={t('contacts.description')} />
            </Form.Item>
          )}
        </Col>
      </Row>
    </Form>
  )
}
